
import React, { useState } from "react";
import { ImagePath } from "../../assests";

interface GoogleReview {
  authorName: string;
  authorUri?: string;
  authorPhotoUri?: string;
  rating: number;
  text: string;
  relativeTime: string;
}

interface GoogleReviewsData {
  businessName: string;
  rating: number;
  reviewCount: number;
  googleMapsUri?: string;
  reviews: GoogleReview[];
}

interface GoogleReviewsCarouselProps {
  placeId?: string;
}

// Static review data — no Google API calls
const STATIC_REVIEWS: GoogleReviewsData = {
  businessName: "Petland",
  rating: 4.9,
  reviewCount: 84,
  googleMapsUri: "",
  reviews: [
    {
      authorName: "Julia Macrae",
      rating: 5,
      relativeTime: "6 months ago",
      text: "Fantastic experience from start to finish! The team was incredibly helpful from my first enquiry to finding my perfect match. The shop was clean, the animals clearly well cared for, and the staff were knowledgeable and friendly.",
      authorPhotoUri: "",
    },
    {
      authorName: "Marie-Morisawa Grace",
      rating: 5,
      relativeTime: "7 months ago",
      text: "I had such an amazing experience at Petland Shop and Veterinary Clinic here in the UAE! From the first visit, I was impressed by how professional, welcoming, and organized everything was.",
      authorPhotoUri: "",
    },
    {
      authorName: "Fatima Anoohi",
      rating: 5,
      relativeTime: "6 months ago",
      text: "I had such a unique and wonderful experience getting my dog from Petland. The team was incredibly supportive from day one, not just during the process but even after bringing my puppy home. Their care, guidance, and genuine passion really made the experience special.",
      authorPhotoUri: "",
    },
    {
      authorName: "Gemma Roberts",
      rating: 5,
      relativeTime: "4 months ago",
      text: "First visit to Petland and I was so impressed! Staff were so friendly and knowledgeable. A special shout out to Orlando who looked after us today for Luna's summer groom. We will definitely be returning. Thank you!",
      authorPhotoUri: "",
    },
    {
      authorName: "Elena V",
      rating: 5,
      relativeTime: "5 months ago",
      text: "Wonderful service and a lovely team! Everyone was friendly, professional, and helpful. My pet was treated with so much care, and the entire experience was excellent.",
      authorPhotoUri: "",
    },
    {
      authorName: "Sarah Williams",
      rating: 5,
      relativeTime: "3 months ago",
      text: "Amazing customer service and a great experience at Petland. The staff answered all my questions and helped me choose the right products for my pet. Highly recommended!",
      authorPhotoUri: "",
    },
  ],
};

const GoogleReviewsCarousel: React.FC<
  GoogleReviewsCarouselProps
> = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const data = STATIC_REVIEWS;

  const nextReview = () => {
    setCurrentIndex((prev) =>
      (prev + 1) % data.reviews.length
    );
  };

  const previousReview = () => {
    setCurrentIndex((prev) =>
      prev === 0
        ? data.reviews.length - 1
        : prev - 1
    );
  };

  const renderStars = (
    rating: number,
    size = "text-[18px]"
  ) => (
    <div className="flex items-center gap-[2px]">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`${size} leading-none ${
            star <= Math.round(rating)
              ? "text-[#FABB05]"
              : "text-gray-300"
          }`}
        >
          ★
        </span>
      ))}
    </div>
  );

  const getInitials = (name: string) => {
    const words = name.trim().split(/\s+/);

    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return (
      words[0].charAt(0) +
      words[words.length - 1].charAt(0)
    ).toUpperCase();
  };

  const visibleReviews = [0, 1, 2].map(
    (offset) =>
      data.reviews[
        (currentIndex + offset) % data.reviews.length
      ]
  );

  return (
    <section className="relative w-full overflow-hidden bg-white py-10 sm:py-14">
      {/* Background decoration */}
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-[#F7F1E9] opacity-60 blur-3xl" />

      <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-[#EEF5E8] opacity-60 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="mb-5 flex justify-center">
            <img
              src={ImagePath.Logo}
              alt="Petland"
              className="h-auto w-[150px] object-contain sm:w-[175px]"
            />
          </div>

          <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl lg:text-[38px]">
            <span className="text-[#B4875B]">
              What People Say
            </span>{" "}
            About Petland
          </h2>

          <div className="mx-auto mt-4 flex items-center justify-center gap-3">
            <span className="h-px w-12 bg-[#B4875B]/40" />
            <span className="text-[#56843C]">✦</span>
            <span className="h-px w-12 bg-[#B4875B]/40" />
          </div>

          <p className="mx-auto mt-3 max-w-xl text-sm text-gray-500 sm:text-base">
            Real reviews from our happy Petland family.
          </p>
        </div>

        {/* Rating + Reviews */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
          {/* Rating Card */}
          <div className="w-full shrink-0 lg:w-[220px]">
            <div className="rounded-3xl border border-[#E9E3DA] bg-white p-6 text-center shadow-[0_8px_30px_rgba(74,58,40,0.08)]">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-[#EEE7DE] bg-white shadow-sm">
                <img
                  src={ImagePath.Logo}
                  alt="Petland"
                  className="h-14 w-14 object-contain"
                />
              </div>

              <h3 className="text-lg font-bold text-gray-900">
                {data.businessName}
              </h3>

              <div className="mt-2 flex justify-center">
                {renderStars(data.rating, "text-[21px]")}
              </div>

              <div className="mt-2">
                <span className="text-2xl font-extrabold text-gray-900">
                  {data.rating.toFixed(1)}
                </span>
                <span className="ml-1 text-xs text-gray-400">
                  / 5
                </span>
              </div>

              <p className="mt-1 text-sm text-gray-500">
                {data.reviewCount.toLocaleString()} reviews
              </p>

             
            </div>
          </div>

          {/* Review Carousel */}
          <div className="relative min-w-0 flex-1">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {visibleReviews.map((review, index) => (
                <ReviewCard
                  key={`${review.authorName}-${currentIndex}-${index}`}
                  review={review}
                  renderStars={renderStars}
                  getInitials={getInitials}
                  className={
                    index > 0
                      ? "hidden sm:flex"
                      : ""
                  }
                />
              ))}
            </div>

            {data.reviews.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={previousReview}
                  aria-label="Previous reviews"
                  className="absolute -left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-xl text-gray-600 shadow-lg transition hover:scale-105 lg:-left-5"
                >
                  ←
                </button>

                <button
                  type="button"
                  onClick={nextReview}
                  aria-label="Next reviews"
                  className="absolute -right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-xl text-gray-600 shadow-lg transition hover:scale-105 lg:-right-5"
                >
                  →
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

interface ReviewCardProps {
  review: GoogleReview;
  renderStars: (
    rating: number,
    size?: string
  ) => React.ReactNode;
  getInitials: (name: string) => string;
  className?: string;
}

const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  renderStars,
  getInitials,
  className = "",
}) => {
  const [expanded, setExpanded] = useState(false);

  const isLongReview = review.text.length > 180;

  const displayText =
    !expanded && isLongReview
      ? `${review.text.slice(0, 180)}...`
      : review.text;

  return (
    <div
      className={`group relative flex min-h-[285px] flex-col rounded-3xl border border-[#EEEAE4] bg-white p-5 shadow-[0_5px_25px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_35px_rgba(0,0,0,0.10)] ${className}`}
    >
      {/* Reviewer */}
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EEF4E9] text-sm font-bold text-[#56843C]">
          {getInitials(review.authorName)}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-gray-900">
            {review.authorName}
          </p>
          <p className="mt-0.5 text-xs text-gray-400">
            {review.relativeTime}
          </p>
        </div>

       
      </div>

      {/* Stars */}
      <div className="mt-4">
        {renderStars(review.rating, "text-[17px]")}
      </div>

      {/* Review text */}
      <div className="mt-4 flex-1">
        <p className="text-sm leading-[1.65] text-gray-700">
          {displayText}
        </p>

        {isLongReview && (
          <button
            type="button"
            onClick={() =>
              setExpanded((prev) => !prev)
            }
            className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#56843C] hover:text-[#3F652E]"
          >
            {expanded ? "Read less" : "Read more"}
            <span>{expanded ? "‹" : "›"}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default GoogleReviewsCarousel;
